import React, { useState, useEffect, useRef } from "react";
import { Input, Table, Tag, Select, Switch, Tooltip } from "antd"; // Removed Tooltip if not used elsewhere
import { SearchOutlined } from "@ant-design/icons";
import { colors } from "./../../devdata/constants/themes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUp,
  faArrowDown,
  faStar,
  faSearch,
} from "@fortawesome/free-solid-svg-icons"; // Cleaned up icons
import PiScoreTag from "../piScoreTag";
import { Statescontex, useUserContext } from "../../context";
import { Demohai, Subhai, Algotester } from "..";
import { stocks } from "../../devdata/maps/chartred";
import classNames from "classnames";
import { whaticonstocks } from "../../devdata/maps/whaticon";
import { Button, Modal } from "antd";
import PiStatusBadgeWithTrend from "../piStatusBadgeWithTrend";
const DhanIcon = require("../../devdata/icons/dhanLogo.png");
const AlgoIcon = require("../../devdata/icons/algoTestLogo.jpg");
const PiTableCard = ({ title, description, bullorbear }) => {
  const { setdaystocks, daystocks, isDhanLogged, dhanToken, userdetails } = useUserContext();
  const [loading, setLoading] = useState(false);
  const { setErrorMessage, setErrorshow } = Statescontex();
  const [changeSortOrder, setChangeSortOrder] = useState("descend");
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [data, setdata] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState("All Sectors");
  const [showGoodToEnter, setShowGoodToEnter] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [recordit, setrecordit] = useState()
  // --- New: Hook to measure container width for filters ---
  const filterControlsRef = useRef(null);
  const [filterContainerWidth, setFilterContainerWidth] = useState(0);

  const handleCloseSearch = () => {
    if (!searchValue) {
      setSearchVisible(false);
      handleSearch("");
    }
  };

  useEffect(() => {
    if (filterControlsRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setFilterContainerWidth(entry.contentRect.width);
        }
      });
      resizeObserver.observe(filterControlsRef.current);
      return () => resizeObserver.disconnect();
    }
  }, []);

  // Set a breakpoint threshold (adjust as needed)
  const isFilterSmall = filterContainerWidth < 600;
  // ---------------------

  // --- Fetching and Initial Sorting Logic ---
  const fetchAndPrepareData = () => {
    setLoading(true);
    try {
      let d;
      if (bullorbear) {
        d = sessionStorage.getItem("pibull");
      } else {
        d = sessionStorage.getItem("pibear");
      }

      if (d) {
        const parseddata = JSON.parse(d);

        if (bullorbear) {
          parseddata.sort((a, b) => b.per_chg - a.per_chg);
          parseddata.map((stock) => {
            stock.pil = stock.pibull;
          });
        } else {
          parseddata.sort((a, b) => a.per_chg - b.per_chg);
          parseddata.map((stock) => {
            stock.pil = stock.pibear;
          });
        }
        const uniqueSectors = Array.from(
          new Set(parseddata.map((stock) => stock.sector).filter(Boolean))
        );
        setSectors(uniqueSectors);
        applyFilters(
          parseddata,
          selectedSector,
          showGoodToEnter,
          searchValue,
          changeSortOrder
        ); // Pass initial sort order
      } else {
        setdata([]);
        setFilteredData([]);
        setSectors([]);
      }
    } catch (error) {
      console.error("Error fetching or parsing data:", error);
      setErrorMessage("h_Failed to fetch or process data");
      setErrorshow(true);
      setdata([]);
      setFilteredData([]);
      setSectors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAndPrepareData();
    let intervalId;

    intervalId = setInterval(() => {
      fetchAndPrepareData();
    }, 1000);

    return () => clearInterval(intervalId);
  }, [selectedSector, showGoodToEnter, searchValue, changeSortOrder]); // Removed dependencies causing potential loops

  // --- Filtering and Sorting Logic ---
  const applyFilters = (sourceData, sector, showGood, search, sortOrder) => {
    let intermediateData = [...sourceData];

    if (showGood) {
      intermediateData = intermediateData.filter((stock) => stock.pistatus === 2);
    }
    if (sector !== "All Sectors") {
      intermediateData = intermediateData.filter(
        (stock) => stock.sector === sector
      );
    }
    if (search) {
      intermediateData = intermediateData.filter((stock) =>
        stock.name.toLowerCase().startsWith(search.toLowerCase())
      );
    }
    if (sortOrder) {
      // Apply final sort order
      sortData(intermediateData, "pil", sortOrder); // Sort the filtered data
      return;
    }
    setFilteredData(intermediateData);
  };

  const sortData = (dataToSort, key, direction) => {
    const sorted = [...dataToSort].sort((a, b) => {
      const aValue = a[key];
      const bValue = b[key];
      // Handle potential non-numeric values during sort
      const numA = typeof aValue === "number" ? aValue : -Infinity;
      const numB = typeof bValue === "number" ? bValue : -Infinity;
      return direction === "ascend" ? numA - numB : numB - numA;
    });

    setFilteredData(sorted);
  };

  // --- Event Handlers ---
  const toggleFavorite = (key, event) => {
    event.stopPropagation();
    setdaystocks((prev) => {
      const updated = new Set(prev);
      if (updated.has(key)) updated.delete(key);
      else updated.add(key);
      return Array.from(updated);
    });
  };

  const handleSortChange = () => {
    const newOrder = changeSortOrder === "ascend" ? "descend" : "ascend";
    setChangeSortOrder(newOrder);
    // Re-apply filters and sort with the new order
    applyFilters(data, selectedSector, showGoodToEnter, searchValue, newOrder);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    applyFilters(data, selectedSector, showGoodToEnter, value, changeSortOrder);
  };

  const handleSectorChange = (value) => {
    setSelectedSector(value);
    applyFilters(data, value, showGoodToEnter, searchValue, changeSortOrder);
  };

  const handleGoodToEnterToggle = (checked) => {
    setShowGoodToEnter(checked);
    applyFilters(data, selectedSector, checked, searchValue, changeSortOrder);
  };

  const handleRowClick = (record) => {
    const found = stocks.find((stock) => stock.nsecode === record.nsecode);
    const foundUrl = isDhanLogged
      ? found?.dhanUrl?.replace("123GOFREE", dhanToken)
      : found?.url || found?.url;
    if (foundUrl) {
      window.open(foundUrl, "_blank");
    } else {
      console.warn(`URL not found for NSE code: ${record.nsecode}`);
      setErrorMessage(`h_Details URL not available for ${record.name}`);
      setErrorshow(true);
    }
  };

  const handleAlgotestClick = (record) => {
    // console.log(record);

    setrecordit(record)
  };
  // --- Column Definitions ---
  const columns = [
    {
      title: "",
      dataIndex: "nsecode",
      key: "favorite",
      width: 45, // Slightly more space for click target
      render: (key) => (
        <div
          className="p-2 cursor-pointer flex items-center justify-center"
          onClick={(e) => toggleFavorite(key, e)}
        >
          <FontAwesomeIcon
            icon={faStar}
            className={`text-base transition-colors duration-200 ${daystocks.includes(key)
              ? "text-yellow-500"
              : "text-darkMode-layer3 hover:text-yellow-300"
              }`}
          />
        </div>
      ),
    },
    {
      title: "Stock",
      dataIndex: "name",
      key: "name",
      width: 180, // Increased fixed width for Stock name + icon
      // fixed: "left", // <<< FIX COLUMN LEFT
      render: (text, record) => (
        <div className="flex items-center gap-2">
          {whaticonstocks[record.name] ? (
            <img
              src={whaticonstocks[record.name]}
              alt={`${text} icon`}
              className="w-7 h-7 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-7 h-7 bg-darkMode-layer3 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-sm">?</span>
            </div>
          )}
          <span
            className="text-xs sm:text-sm line-clamp-2 cursor-pointer"
            onClick={() => handleRowClick(record)}
          >
            {text}
          </span>
        </div>
      ),
    },
    {
      title: "Sector",
      dataIndex: "sector",
      key: "sector",
      align: "center",
      width: 130, // Adjusted width
      ellipsis: true,
      render: (text) => (
        <span className="text-xs sm:text-sm px-1">{text || "-"}</span>
      ),
    },
    {
      title: (
        <div className="flex items-center justify-center">
          <span className="text-xs sm:text-sm">% Chg</span>
        </div>
      ),
      dataIndex: "per_chg",
      key: "per_chg",
      align: "center",
      width: 90, // Adjusted width
      render: (text) => (
        <Tag
          className={classNames(
            "rounded-full bg-opacity-20 border-none text-xs font-semibold py-0.5 px-1.5",
            {
              "bg-darkMode-green text-darkMode-green": text > 0,
              "bg-darkMode-red text-darkMode-red": text < 0,
              "bg-gray-500 text-gray-300":
                text === 0 || typeof text !== "number",
            }
          )}
        >
          {typeof text === "number" ? `${text.toFixed(2)}%` : "-"}
        </Tag>
      ),
    },
    {
      title: "Pi Score",
      dataIndex: "pil",
      key: "pil",
      align: "center",
      render: (text) => <PiScoreTag value={text} />,
    },
    {
      title: <span className="text-xs sm:text-sm">Suggestion</span>,
      dataIndex: "pistatus",
      key: "good",
      align: "center",
      width: 120, // Adjusted width, needs enough space for the tag

      render: (_: any, row: any) => (
        <PiStatusBadgeWithTrend
          pistatus={row.pistatus}
          trendBadge={row.trendBadge}
        />
      ),
    },
    {
      title: <span className="text-xs sm:text-sm">Automate</span>,
      key: "actions",
      align: "center",
      width: 80, // Adjusted width, needs enough space for the tag
      render: (record) => {
        return (
          <div className="flex items-center justify-center w-full gap-2 px-1">
            <Button shape="circle" size="small">
              <img src={DhanIcon} alt="Dhan" className="w-full h-full" />
              <Tooltip title="Coming Soon">
                <div className="bg-gray-600 absolute w-full h-full rounded-full opacity-80 z-50"></div>
              </Tooltip>
            </Button>
            <Button shape="circle" size="small" >
              {/* */}
              <img src={AlgoIcon} alt="Algo Test" className="w-full h-full" onClick={() => handleAlgotestClick(record)} />
              {/* <Tooltip title="Coming Soon">
                <div className="bg-gray-600 absolute w-full h-full rounded-full opacity-80 z-50"></div>
              </Tooltip> */}
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="relative p-3 sm:p-4 rounded-xl bg-darkMode-layer1 shadow-lg">
      {/* Heading & Description */}
      <div className="flex flex-row justify-between items-center sm:items-center mb-3 sm:mb-4">
        <div className="mb-2 sm:mb-0">
          <h2 className="text-lg sm:text-xl font-semibold text-darkMode-text">
            {title}
          </h2>
          <p className="text-xs sm:text-sm text-darkMode-subText">
            {description}
          </p>
        </div>
        <div className="flex-shrink-0">
          <Demohai what="video url for stock analysis by sectors" />
        </div>
      </div>

      {/* --- Responsive Filters & Search Section --- */}
      <div ref={filterControlsRef} className="mb-4 sm:mb-6">
        {isFilterSmall ? (
          <>
            {/* First row: Good to Enter toggle */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-3 text-darkMode-text flex-shrink-0">
                <span className="text-sm sm:text-base">Show only</span>
                <div className="rounded-full bg-darkMode-layer3 text-sm sm:text-base font-semibold text-darkMode-green px-4 py-1 whitespace-nowrap">
                  <span>Good to Enter</span>
                </div>
                <Switch
                  className="transition-all duration-200 scale-110"
                  checked={showGoodToEnter}
                  onChange={handleGoodToEnterToggle}
                  style={{
                    backgroundColor: showGoodToEnter
                      ? colors.darkMode.greenDarker
                      : "",
                  }}
                />
              </div>
            </div>
            {/* Second row: Sector and Search controls */}
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 w-full justify-end">
              <div className="text-darkMode-text w-auto max-w-[180px] sm:max-w-[200px] flex-grow sm:flex-grow-0">
                <Select
                  value={selectedSector}
                  style={{
                    width: "100%",
                    minWidth: "100px",
                    textAlign: "center",
                    height: "40px",
                  }}
                  onChange={handleSectorChange}
                  options={[
                    { label: "All Sectors", value: "All Sectors" },
                    ...sectors.map((sector) => ({
                      label: sector,
                      value: sector,
                    })),
                  ]}
                  placeholder="Select Sector"
                  aria-label="Filter by Sector"
                />
              </div>
              <div className="text-white w-auto max-w-[180px] sm:max-w-[200px] flex-grow sm:flex-grow-0">
                {searchVisible ? (
                  <Input
                    className="h-[40px] text-white text-xs sm:text-sm px-3 py-2 rounded-lg bg-darkMode-layer2 shadow-sm transition-all duration-300 ease-in-out border-none hover:ring-1 hover:ring-darkMode-green focus:ring-1 focus:ring-darkMode-green w-full"
                    placeholder="Search Stock..."
                    prefix={
                      <SearchOutlined className="text-darkMode-subText mr-1" />
                    }
                    value={searchValue}
                    onChange={(e) => handleSearch(e.target.value)}
                    allowClear
                    autoFocus
                    onBlur={handleCloseSearch}
                    onPressEnter={handleCloseSearch}
                  />
                ) : (
                  <button
                    className="flex items-center justify-center h-[40px] text-xs sm:text-sm px-3 py-2 rounded-lg bg-darkMode-layer2 shadow-sm transition-all duration-300 ease-in-out hover:ring-1 hover:ring-darkMode-green text-darkMode-subText hover:text-darkMode-text w-full"
                    onClick={() => setSearchVisible(true)}
                    aria-label="Open search input"
                  >
                    <FontAwesomeIcon icon={faSearch} className="mr-1.5" />
                    <span>Search</span>
                  </button>
                )}
              </div>
            </div>
          </>
        ) : (
          // Wide layout: Everything in one row.
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3 text-darkMode-text flex-shrink-0">
              <span className="text-sm sm:text-base">Show only</span>
              <div className="rounded-full bg-darkMode-layer3 text-sm sm:text-base font-semibold text-darkMode-green px-4 py-1 whitespace-nowrap">
                <span>Good to Enter</span>
              </div>
              <Switch
                size="default"
                className="transition-all duration-200 scale-110"
                checked={showGoodToEnter}
                onChange={handleGoodToEnterToggle}
                style={{
                  backgroundColor: showGoodToEnter
                    ? colors.darkMode.greenDarker
                    : "",
                }}
              />
            </div>
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 w-full sm:w-auto justify-end">
              <div className="text-darkMode-text w-auto max-w-[180px] sm:max-w-[200px] flex-grow sm:flex-grow-0">
                <Select
                  value={selectedSector}
                  style={{
                    width: "100%",
                    minWidth: "100px",
                    textAlign: "center",
                    height: "40px",
                  }}
                  onChange={handleSectorChange}
                  options={[
                    { label: "All Sectors", value: "All Sectors" },
                    ...sectors.map((sector) => ({
                      label: sector,
                      value: sector,
                    })),
                  ]}
                  placeholder="Select Sector"
                  aria-label="Filter by Sector"
                />
              </div>
              <div className="text-darkMode-text w-auto max-w-[180px] sm:max-w-[200px] flex-grow sm:flex-grow-0">
                {searchVisible ? (
                  <Input
                    className="h-[40px] text-sm sm:text-sm px-3 py-2 rounded-lg bg-darkMode-layer2 shadow-sm transition-all duration-300 ease-in-out border-none hover:ring-1 hover:ring-darkMode-green focus:ring-1 focus:ring-darkMode-green w-full"
                    placeholder="Search Stock..."
                    prefix={
                      <SearchOutlined className="text-darkMode-subText mr-1" />
                    }
                    value={searchValue}
                    onChange={(e) => handleSearch(e.target.value)}
                    allowClear
                    autoFocus
                    onBlur={handleCloseSearch}
                    onPressEnter={handleCloseSearch}
                  />
                ) : (
                  <button
                    className="flex items-center justify-center h-[40px] text-xs sm:text-sm px-3 py-2 rounded-lg bg-darkMode-layer2 shadow-sm transition-all duration-300 ease-in-out hover:ring-1 hover:ring-darkMode-green text-darkMode-subText hover:text-darkMode-text w-full"
                    onClick={() => setSearchVisible(true)}
                    aria-label="Open search input"
                  >
                    <FontAwesomeIcon icon={faSearch} className="mr-1.5" />
                    <span>Search</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* --- End Responsive Filters & Search Section --- */}

      {/* --- Responsive Table Wrapper --- */}
      <div className="relative w-full overflow-x-auto min-h-[300px]">
        <Subhai />
        <Table
          dataSource={filteredData}
          columns={columns}
          className="rounded-lg overflow-hidden text-darkMode-text"
          rowClassName="hover:bg-darkMode-layer2 transition-colors duration-150 group cursor-default"
          size="middle"
          loading={loading}
          pagination={{
            defaultPageSize: 10,
            total: filteredData.length,
            responsive: true,
            showSizeChanger: false,
            className: "pt-3",
          }}
          scroll={{ x: "max-content" }}
          style={{ backgroundColor: "transparent" }}
          rowKey="nsecode"
        />
      </div>
      <Algotester userdetails={userdetails} recordit={recordit} />
    </div>
  );
};

export default PiTableCard;
